import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container,
  Fab,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Add, MoreVert } from "@material-ui/icons";
import { FormattedMessage, useIntl } from "react-intl";

import Title from "../../ui/Title";
import FloatingActions from "../../ui/FloatingActions";
import Confirm from "../../ui/Confirm";

import { confirm as confirmMsgs, account } from "../../messages";
import { useBmapi } from "../../utils/bmapi-context";
import { getErrorMessageString } from "../../utils/errors";
import { courses as coursesMsgs } from "../../messages/courses";
import GenListDropdown from "../../components/genlist/GenListDropdown";
import FreeTextChipsInput from "../../components/FreeTextChipsInput";
import MultipleSelectionsWithChip from "../../components/MultipleSelectionWithChip";
import CampusCourseEntryMode from "../../components/CampusCourseEntryMode";

const useStyles = makeStyles(() => ({
  primaryClamp2: {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  chipRow: {
    marginTop: 4,
    display: "flex",
    flexWrap: "wrap",
    "& > *": {
      marginRight: 6,
      marginBottom: 6,
    },
  },
  fabWrap: { position: "relative", display: "inline-flex" },
  fabProgress: {
    position: "absolute",
    top: -6,
    right: -6,
    zIndex: 1,
  },
}));

const LANGUAGE_OPTIONS = [
  { value: "it", label: "Italiano" },
  { value: "en", label: "Inglese" },
  { value: "fr", label: "Francese" },
  { value: "de", label: "Tedesco" },
];

const ACCESS_OPTIONS = [
  { value: "onsite", label: "In presenza" },
  { value: "online", label: "Telematica" },
];

export default function ManageCampusCourses() {
  const classes = useStyles();
  const intl = useIntl();
  const { bmapi, notifyError, notifySuccess, businessId } = useBmapi();

  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  // conferme
  const [deleteRequest, setDeleteRequest] = useState(false);
  const [closeEditorConfirm, setCloseEditorConfirm] = useState(false);

  // anno (int) 2024 -> current+3
  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const yearOptions = useMemo(() => {
    const max = currentYear + 3;
    const out = [];
    for (let y = 2024; y <= max; y++) out.push(y);
    return out;
  }, [currentYear]);

  // editor popup
  const [editor, setEditor] = useState({
    open: false,
    mode: "create", // 'create' | 'edit'
    id: null,
    values: {
      year: currentYear, // int
      name: "",
      description: "",
      url: "",
      area: [],
      geo_area: [],
      locations: [],
      course_langs: ["it"], // default
      entry_mode: "", // string
      access: [], // <-- nuovo campo (array di stringhe)
    },
    errors: {
      year: false,
      name: false,
      description: false,
      url: false,
      area: false,
      geo_area: false,
      locations: false,
      course_langs: false,
      entry_mode: false,
      access: false,
    },
    dirty: false,
    busy: false,
  });

  const loadCoursesList = useCallback(() => {
    if (!bmapi) return;
    bmapi
      .getCourses({
        business: businessId,
        lang: bmapi.getUserInfo().business.default_language,
      })
      .then((resp) =>
        setList(
          (resp || []).slice().sort((a, b) =>
            (a?.name ?? "").localeCompare(b?.name ?? "", undefined, {
              sensitivity: "base",
            })
          )
        )
      )
      .catch((e) => notifyError(getErrorMessageString(e, intl)));
  }, [bmapi, intl, notifyError, businessId]);

  useEffect(() => {
    loadCoursesList();
  }, [loadCoursesList]);

  // MENU
  const handleMenuOpen = (event, id) => {
    setAnchorEl(event.currentTarget);
    setSelectedId(id);
  };
  const handleMenuClose = () => setAnchorEl(null);

  // EDITOR
  const openCreateEditor = () =>
    setEditor({
      open: true,
      mode: "create",
      id: null,
      values: {
        year: currentYear,
        name: "",
        description: "",
        url: "",
        area: [],
        geo_area: [],
        locations: [],
        course_langs: ["it"],
        entry_mode: "",
        access: [],
      },
      errors: {
        year: false,
        name: false,
        description: false,
        url: false,
        area: false,
        geo_area: false,
        locations: false,
        course_langs: false,
        entry_mode: false,
        access: false,
      },
      dirty: false,
      busy: false,
    });

  const openEditEditor = (id) => {
    const item = list.find((x) => x.id === id);
    setEditor({
      open: true,
      mode: "edit",
      id,
      values: {
        year: Number(item?.year) || currentYear,
        name: item?.name ?? "",
        description: item?.description ?? "",
        url: item?.url ?? "",
        area: Array.isArray(item?.area) ? item.area : [],
        geo_area: Array.isArray(item?.geo_area) ? item.geo_area : [],
        locations: Array.isArray(item?.locations) ? item.locations : [],
        course_langs:
          Array.isArray(item?.course_langs) && item.course_langs.length
            ? item.course_langs
            : ["it"],
        entry_mode: item?.entry_mode ?? "",
        access: Array.isArray(item?.access) ? item.access : [], // <-- carica access
      },
      errors: {
        year: false,
        name: false,
        description: false,
        url: false,
        area: false,
        geo_area: false,
        locations: false,
        course_langs: false,
        entry_mode: false,
        access: false,
      },
      dirty: false,
      busy: false,
    });
  };

  const requestCloseEditor = () => {
    if (editor.busy) return;
    if (editor.dirty) {
      setCloseEditorConfirm(true);
      return;
    }
    setEditor((s) => ({ ...s, open: false }));
  };

  const confirmCloseEditor = () => {
    setCloseEditorConfirm(false);
    setEditor((s) => ({ ...s, open: false, dirty: false }));
  };

  const handleField = (field) => (e) => {
    const val = e.target.value;
    setEditor((s) => ({
      ...s,
      values: { ...s.values, [field]: val },
      dirty: true,
      errors: { ...s.errors, [field]: false },
    }));
  };

  // year come number
  const handleYear = (e) => {
    const val = Number(e.target.value);
    setEditor((s) => ({
      ...s,
      values: { ...s.values, year: val },
      dirty: true,
      errors: { ...s.errors, year: false },
    }));
  };

  const validate = () => {
    const nameInvalid = !editor.values.name?.trim();
    const urlStr = editor.values.url?.trim() || "";
    const urlInvalid = urlStr.length > 0 && !/^https?:\/\//i.test(urlStr); // semplice check

    const errs = {
      year: false,
      name: nameInvalid,
      description: false,
      url: urlInvalid,
      area: false,
      geo_area: false,
      locations: false,
      course_langs: false,
      entry_mode: false,
      access: false,
    };
    setEditor((s) => ({ ...s, errors: errs }));
    return !nameInvalid && !urlInvalid;
  };

  const saveEditor = async () => {
    if (!bmapi) return;
    if (!validate()) return;

    setEditor((s) => ({ ...s, busy: true }));
    try {
      const payload = {
        year: Number(editor.values.year) || currentYear, // int
        name: editor.values.name.trim(),
        description: editor.values.description?.trim() ?? "",
        url: editor.values.url?.trim() ?? "",
        area: Array.isArray(editor.values.area) ? editor.values.area : [],
        geo_area: Array.isArray(editor.values.geo_area)
          ? editor.values.geo_area
          : [],
        locations: Array.isArray(editor.values.locations)
          ? editor.values.locations
          : [],
        course_langs: Array.isArray(editor.values.course_langs)
          ? editor.values.course_langs
          : ["it"],
        entry_mode: editor.values.entry_mode ?? "",
        access: Array.isArray(editor.values.access) ? editor.values.access : [], // <-- invia access
      };

      if (editor.mode === "edit" && editor.id) {
        await bmapi.updateCourse(editor.id, payload);
      } else {
        await bmapi.createCourse(payload);
      }

      notifySuccess(intl.formatMessage(account.saveConfirm));
      setEditor((s) => ({ ...s, busy: false, open: false, dirty: false }));
      loadCoursesList();
    } catch (e) {
      setEditor((s) => ({ ...s, busy: false }));
      notifyError(getErrorMessageString(e, intl));
    }
  };

  // DELETE
  const handleDeleteClick = () => {
    setDeleteRequest(true);
    handleMenuClose();
  };
  const onDeleteCourse = async () => {
    if (!selectedId) return;
    try {
      await bmapi.deleteCourse(selectedId);
      notifySuccess(intl.formatMessage(account.deleteConfirm));
      setDeleteRequest(false);
      setSelectedId(null);
      loadCoursesList();
    } catch (e) {
      notifyError(getErrorMessageString(e, intl));
    }
  };

  return (
    <Container maxWidth="md">
      <Title>
        <FormattedMessage
          id="navigation.manager.courses"
          defaultMessage="Corsi"
        />
      </Title>

      <List>
        {list.map((course, idx) => (
          <React.Fragment key={course.id}>
            {idx > 0 && <Divider component="li" />}
            <ListItem>
              <ListItemText
                primary={course.name}
                primaryTypographyProps={{ className: classes.primaryClamp2 }}
                secondary={
                  Array.isArray(course.locations) &&
                  course.locations.length > 0 && (
                    <div className={classes.chipRow}>
                      {course.locations.map((loc, i) => (
                        <Chip key={`${loc}-${i}`} label={loc} size="small" />
                      ))}
                    </div>
                  )
                }
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="menu"
                  onClick={(e) => handleMenuOpen(e, course.id)}
                >
                  <MoreVert />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          </React.Fragment>
        ))}
      </List>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            openEditEditor(selectedId);
            handleMenuClose();
          }}
        >
          Modifica
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>Elimina</MenuItem>
      </Menu>

      {/* Editor popup */}
      <Dialog
        open={editor.open}
        onClose={requestCloseEditor}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editor.mode === "edit"
            ? intl.formatMessage(
                coursesMsgs.editCourse || {
                  id: "courses.edit",
                  defaultMessage: "Modifica corso",
                }
              )
            : intl.formatMessage(
                coursesMsgs.addCourse || {
                  id: "courses.add",
                  defaultMessage: "Nuovo corso",
                }
              )}
        </DialogTitle>
        <DialogContent>
          {/* ANNO (int) */}
          <TextField
            select
            margin="dense"
            label="Anno"
            value={editor.values.year ?? currentYear}
            onChange={handleYear}
            fullWidth
            variant="standard"
          >
            {yearOptions.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </TextField>

          {/* NOME */}
          <TextField
            autoFocus
            margin="dense"
            label="Nome"
            value={editor.values.name}
            onChange={handleField("name")}
            fullWidth
            variant="standard"
            error={editor.errors.name}
            helperText={editor.errors.name ? "Obbligatorio" : ""}
            disabled={editor.busy}
          />

          {/* LOCATIONS */}
          <FreeTextChipsInput
            label="Comuni/Località"
            placeholder="Aggiungi e premi Invio"
            value={
              Array.isArray(editor.values.locations)
                ? editor.values.locations
                : []
            }
            onChange={(next) =>
              handleField("locations")({ target: { value: next } })
            }
            disabled={editor.busy}
            margin="dense"
            maxItems={50}
            maxLength={120}
          />

          {/* AREA GEOGRAFICA */}
          <GenListDropdown
            onChange={handleField("geo_area")}
            margin="dense"
            label="Regione/Area geografica"
            listId="business_locations"
            value={
              Array.isArray(editor.values.geo_area)
                ? editor.values.geo_area
                : []
            }
            name="geo_area"
            multiple={true}
            lang="it"
          />

          {/* LINGUE (course_langs) con chip cancellabili */}
          <MultipleSelectionsWithChip
            label="Lingue"
            name="course_langs"
            options={LANGUAGE_OPTIONS}
            value={
              Array.isArray(editor.values.course_langs)
                ? editor.values.course_langs
                : []
            }
            onChange={handleField("course_langs")}
            margin="dense"
          />

          {/* ACCESSO (entry_mode) — subito dopo le lingue */}
          <CampusCourseEntryMode
            value={editor.values.entry_mode}
            onChange={handleField("entry_mode")}
          />

          {/* MODALITÀ DI EROGAZIONE (access) */}
          <MultipleSelectionsWithChip
            label="Modalità"
            name="access"
            options={ACCESS_OPTIONS}
            value={
              Array.isArray(editor.values.access) ? editor.values.access : []
            }
            onChange={handleField("access")}
            margin="dense"
          />

          {/* URL */}
          <TextField
            margin="dense"
            label="URL"
            value={editor.values.url}
            onChange={handleField("url")}
            fullWidth
            variant="standard"
            error={editor.errors.url}
            helperText={
              editor.errors.url ? "URL non valido: usa http:// o https://" : ""
            }
            disabled={editor.busy}
          />

          {/* DESCRIZIONE */}
          <TextField
            margin="dense"
            label="Descrizione"
            value={editor.values.description}
            onChange={handleField("description")}
            fullWidth
            variant="standard"
            multiline
            rows={3}
            error={editor.errors.description}
            disabled={editor.busy}
          />

          {/* AMBITO */}
          <GenListDropdown
            onChange={handleField("area")}
            margin="dense"
            label="Ambito"
            listId="area"
            value={Array.isArray(editor.values.area) ? editor.values.area : []}
            name="area"
            multiple={true}
            lang="it"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={requestCloseEditor} disabled={editor.busy}>
            Chiudi
          </Button>
          <Box position="relative" display="inline-flex">
            <Button color="primary" onClick={saveEditor} disabled={editor.busy}>
              Salva
            </Button>
            {editor.busy && (
              <CircularProgress
                size={32}
                style={{ position: "absolute", top: -6, right: -10, zIndex: 1 }}
              />
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Conferma chiusura editor con modifiche non salvate */}
      <Confirm
        open={!!closeEditorConfirm}
        onConfirm={confirmCloseEditor}
        onCancel={() => setCloseEditorConfirm(false)}
        text={
          intl.formatMessage?.(
            confirmMsgs?.exitPrompt || {
              id: "confirm.exitPrompt",
              defaultMessage: "Hai modifiche non salvate. Uscire comunque?",
            }
          ) || "Hai modifiche non salvate. Uscire comunque?"
        }
      />

      {/* Conferma eliminazione */}
      <Confirm
        open={!!deleteRequest}
        onConfirm={onDeleteCourse}
        onCancel={() => setDeleteRequest(false)}
        text={intl.formatMessage(confirmMsgs.deleteElement)}
      />

      <FloatingActions>
        <span className={classes.fabWrap}>
          <Fab
            variant="extended"
            color="primary"
            onClick={openCreateEditor}
            disabled={editor.busy}
          >
            <Add />
            {intl.formatMessage(
              coursesMsgs.addCourse || {
                id: "courses.add",
                defaultMessage: "Nuovo corso",
              }
            )}
          </Fab>
          {editor.busy && (
            <CircularProgress size={48} className={classes.fabProgress} />
          )}
        </span>
      </FloatingActions>
    </Container>
  );
}
